import { clearCustomerDrafts } from "./customerDrafts";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../Context/UserContext";

/**
 * useLogout — single source of truth for USER-INITIATED sign out.
 *
 * Collapses the previously-duplicated logout handlers in Menu.tsx and
 * ProfilePage.tsx, and powers the new sidebar UserMenu. Returns a stable
 * `logout` callback plus a `signingOut` flag for in-flight UI (spinner /
 * disabled button).
 *
 * NOTE: this is distinct from the *inactivity-timeout* logout in
 * UserContext.tsx, which has different semantics (cross-tab activity check +
 * "session-expired" event). That one intentionally stays separate.
 *
 *   POST /api/logout ──▶ clear user state ──▶ navigate("/login")
 *                  │
 *                  └─ network error: still clear local state (fail-safe:
 *                     the user asked to leave; never trap them logged-in).
 */
export function useLogout(): {
  logout: () => Promise<void>;
  signingOut: boolean;
} {
  const { setUser } = useUserContext();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  // Ref guard so a rapid second click is a true no-op even before the state
  // re-render lands. State drives UI; ref drives control flow.
  const inFlight = useRef(false);

  const logout = useCallback(async () => {
    if (inFlight.current) return; // double-click guard
    inFlight.current = true;
    setSigningOut(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      // Session may already be gone server-side, or the network is down.
      // Either way the user asked to sign out — clear local state in finally.
      console.error(
        "Logout request failed (clearing local state anyway):",
        error
      );
    } finally {
      clearCustomerDrafts();
      setUser(null);
      navigate("/login");
      inFlight.current = false;
      setSigningOut(false);
    }
  }, [setUser, navigate]);

  return { logout, signingOut };
}
